import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaInstagram, FaYoutube, FaWhatsapp, FaFacebook, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const PhotoDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { photos, category } = location.state || {};
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Scroll to the top when the component is mounted
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to check if file is a video
  const isVideo = (filename) => {
    return filename.match(/\.(mp4|webm|ogg)$/i);
  };

  // Animation variants for popup effect (grid items)
  const popupVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05 },
    exit: { scale: 1 },
  };

  // Animation variants for lightbox
  const lightboxVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
  };

  // Animation for lightbox background
  const backdropVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const handleMediaClick = (file) => {
    setSelectedMedia(file); // Open lightbox with selected media
  };

  const handleCloseLightbox = () => {
    setSelectedMedia(null); // Close lightbox
  };

  const handleBackToHome = () => {
    navigate("/photos");
  };

  if (!photos || photos.length === 0) {
    return (
      <>
        <div className="min-h-screen bg-gray-200 px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 lg:p-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 mb-4 sm:mb-0">
              {category || "Category"}
            </h2>
            <button
              onClick={handleBackToHome}
              className="bg-orange-600 text-white text-sm sm:text-base lg:text-lg font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg hover:bg-opacity-80 transition-all duration-300 flex items-center"
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
          </div>
          <div className="flex items-center justify-center h-[calc(100vh-180px)] sm:h-[calc(100vh-200px)]">
            <p className="text-base sm:text-lg text-gray-600 bg-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg shadow-md text-center">
              No media available for {category || "this category"}.
            </p>
          </div>
        </div>
        <footer className="bg-[#292929] text-[#FAFAFA] py-4 text-center w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center gap-4 sm:gap-6 mb-4">
              <a
                href="https://www.instagram.com/new_brindha_studio_?igsh=MXE3ODRwZmJveHdsaw=="
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl sm:text-2xl text-gray-200 hover:text-pink-500 transition-colors duration-300"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a
                href="https://www.youtube.com/@NewBrindhaphotography"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl sm:text-2xl text-gray-200 hover:text-red-600 transition-colors duration-300"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
              <a
                href="https://wa.me/+919698995997"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl sm:text-2xl text-gray-200 hover:text-green-500 transition-colors duration-300"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </a>
              <a
                href="https://www.facebook.com/newbrindhaphotography"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl sm:text-2xl text-gray-200 hover:text-blue-600 transition-colors duration-300"
                aria-label="Facebook"
              >
                <FaFacebook />
              </a>
            </div>
            <p className="text-gray-200 text-sm sm:text-base">© www.newbrindha.com. All rights reserved.</p>
          </div>
        </footer>
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-200 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 lg:p-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 mb-4 sm:mb-0">
            {category}
          </h2>
          <button
            onClick={handleBackToHome}
            className="bg-orange-600 text-white text-sm sm:text-base lg:text-lg font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg hover:bg-opacity-80 transition-all duration-300 flex items-center"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 p-4 sm:p-8 lg:p-12">
          {photos.map((file, index) => {
            const isVideoFile = isVideo(file.filename);

            return (
              <motion.div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer"
                onClick={() => handleMediaClick(file)}
                layout
                transition={{ duration: 0.3 }}
              >
                {isVideoFile ? (
                  <motion.video
                    src={file.file_url}
                    controls
                    className="w-full h-48 sm:h-64 lg:h-72 object-cover"
                    variants={popupVariants}
                    initial="initial"
                    whileHover="hover"
                    animate="exit"
                    transition={{ duration: 0.3 }}
                  />
                ) : (
                  <motion.img
                    src={file.file_url}
                    alt={file.filename}
                    className="w-full h-48 sm:h-64 lg:h-72 object-cover"
                    variants={popupVariants}
                    initial="initial"
                    whileHover="hover"
                    animate="exit"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 sm:p-6"
            variants={backdropVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            onClick={handleCloseLightbox}
          >
            <motion.div
              className="relative w-full max-w-3xl sm:max-w-4xl lg:max-w-5xl h-[70vh] sm:h-[80vh] flex items-center justify-center"
              variants={lightboxVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {isVideo(selectedMedia.filename) ? (
                <motion.video
                  src={selectedMedia.file_url}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                  transition={{ duration: 0.3 }}
                />
              ) : (
                <motion.img
                  src={selectedMedia.file_url}
                  alt={selectedMedia.filename}
                  className="w-full h-full object-contain"
                  transition={{ duration: 0.3 }}
                />
              )}
              <button
                className="fixed top-2 sm:top-4 right-2 sm:right-4 text-gray-200 text-xl sm:text-2xl bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-colors duration-300"
                onClick={handleCloseLightbox}
                aria-label="Close lightbox"
              >
                <FaTimes />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="bg-[#292929] text-[#FAFAFA] py-4 text-center w-full">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-4 sm:gap-6 mb-4">
            <a
              href="https://www.instagram.com/new_brindha_studio_?igsh=MXE3ODRwZmJveHdsaw=="
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl sm:text-2xl text-gray-200 hover:text-pink-500 transition-colors duration-300"
              aria-label="Instagram"
            >
              <FaInstagram />
            </a>
            <a
              href="https://www.youtube.com/@NewBrindhaphotography"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl sm:text-2xl text-gray-200 hover:text-red-600 transition-colors duration-300"
              aria-label="YouTube"
            >
              <FaYoutube />
            </a>
            <a
              href="https://wa.me/+919698995997"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl sm:text-2xl text-gray-200 hover:text-green-500 transition-colors duration-300"
              aria-label="WhatsApp"
            >
              <FaWhatsapp />
            </a>
            <a
              href="https://www.facebook.com/newbrindhaphotography"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl sm:text-2xl text-gray-200 hover:text-blue-600 transition-colors duration-300"
              aria-label="Facebook"
            >
              <FaFacebook />
            </a>
          </div>
          <p className="text-gray-200 text-sm sm:text-base">© www.newbrindha.com. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
};

export default PhotoDetails;